"use client"
import { Share, MessageCircle, UserPlus, Heart, Link, Gift, Coins, MessageSquare } from "lucide-react"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { type Notification, type CryptoData } from "@/lib/api"
import { useTranslation } from 'react-i18next';
import { getGiftLocalizedName } from '@/app/utils/useLocale';
import CryptoIcon from '@/app/components/Dynamic/CryptoIcon';
import { defaultTokenFrom } from '@/app/const/chain';
import { formatDecimalIfNeeded } from "@/lib/utils";
import { formatCoMessageTime } from "@/app/utils/useFormat";

// 消息类型：5(打赏)、6(下单)、7(跟单)
export function NormalItem({
    notification,
    children,
    isDropdown = false,
}: {
    notification: Notification,
    children?: React.ReactNode,
    isDropdown?: boolean,
}) {
    const { t, i18n } = useTranslation();
    const lang = i18n.language;
    const showTimeWithChildren = Boolean(isDropdown && children);
    // 如果是5、6、7，需要处理一下content数据
    let orderInfo = {} as any;
    if ([5, 6, 7].includes(notification.messageType)) {
        orderInfo = JSON.parse(notification.detailData?.roomChatInfo?.content || '')
    }
    const byUser = notification.detailData?.byUser
    return (
        <div className="flex space-x-3 w-full">
            <Avatar 
                onClick={(e) => {
                    e.stopPropagation()
                    if (byUser?.handle) {
                        window.open(`/${byUser.handle}`, '_blank')
                    }
                }} 
                className='cursor-pointer'
            >
                <AvatarImage src={byUser?.avatar} />
                <AvatarFallback>{byUser?.userName?.[0]}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
                {showTimeWithChildren ? (
                    <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="min-w-0 flex items-center gap-2 flex-wrap">{children}</div>
                        <span className="text-xs text-font-primary/50 flex-shrink-0 whitespace-nowrap">
                            {formatCoMessageTime(notification.createTime)}
                        </span>
                    </div>
                ) : null}
                <div className="flex items-center space-x-2 mb-1">
                    {!isDropdown && children}
                    <span
                        className="text-sm font-medium text-font-primary line-clamp-1"
                        onClick={(e) => {
                            e.stopPropagation()
                            if (byUser?.handle) {
                                window.open(`/${byUser.handle}`, '_blank')
                            }
                        }}
                    >
                        {byUser?.userName}
                    </span>
                    <span className="text-xs text-font-primary/70 line-clamp-2">{notification.createAt}</span>
                </div>
                <p className="text-base text-sm mb-2 line-clamp-2 break-all">
                    {[5, 6, 7].includes(notification.messageType) && (
                        <span>
                            {t('explore.inYourLiveRoom')}
                            (
                            <span
                                className="text-blue-500 font-bold hover:underline cursor-pointer "
                                onClick={(e) => {
                                    e.stopPropagation()
                                    window.open(`/live/detail?id=${notification.detailData.roomChatInfo?.roomId}`, '_blank')
                                }}
                            >
                                {notification.detailData.roomChatInfo?.roomName || ""}
                            </span>
                            )

                        </span>
                    )}
                    {notification.messageType === 5 && t('explore.rewarded')}
                    {notification.messageType === 6 && t('explore.placedOrder')}
                    {notification.messageType === 7 && t('explore.followedOrder')}
                </p>
                {notification.messageType === 5 && (
                    <span className='flex items-center align-middle leading-[18px] break-all gap-1 flex-wrap bg-background-3/10 p-3 rounded-lg'>
                        <span className='text-yellow-400 font-bold'>
                            {t('liveDetail.sent')}
                        </span>
                        <img src={
                                orderInfo?.content?.giftGif ||
                                orderInfo?.content?.giftImg ||
                                ''
                            }
                            alt={getGiftLocalizedName(orderInfo?.content, lang)}
                            width={30}
                            height={30}
                            className='rounded-full'
                        />
                        <span className='text-xs text-yellow-400 font-medium'>
                            {getGiftLocalizedName(orderInfo?.content, lang)} x{' '}
                            {orderInfo?.content?.giftQuantity || 0}
                        </span>
                    </span>
                )}
                {[6, 7].includes(notification.messageType) && (
                    <span className='flex items-center align-middle leading-[18px] break-all gap-1 flex-wrap bg-background-3/10 p-3 rounded-lg'>
                        <span className='text-yellow-400 font-bold'>
                            {notification.messageType === 6 ? t('liveDetail.placeOrder') : t('liveDetail.followOrder')}
                        </span>
                        <CryptoIcon
                            coin={
                                {
                                    coinSymbol: orderInfo?.content?.tokenSymbol || '',
                                    coinIcon: orderInfo?.content.coinIcon || '',
                                } as unknown as CryptoData
                            }
                            size={4}
                            className='border border-white'
                        />
                        <span className='text-yellow-400 font-medium'>
                            {orderInfo?.content?.tokenSymbol || ''}
                        </span>
                        {orderInfo?.content?.orderAmount && (
                            <span className='text-yellow-400 text-sm inline-flex items-center break-all'>
                                ({formatDecimalIfNeeded(orderInfo?.content?.orderAmount, 6)}
                                <CryptoIcon
                                    coin={
                                        {
                                            coinSymbol:
                                                orderInfo?.content?.fromTokenSymbol ||
                                                defaultTokenFrom.tokenSymbol,
                                            coinIcon:
                                                orderInfo?.content?.fromTokenLogoUrl ||
                                                defaultTokenFrom.tokenLogoUrl,
                                        } as unknown as CryptoData
                                    }
                                    size={3}
                                    className='ml-1 mr-1 border border-white'
                                />
                                {orderInfo?.content?.fromTokenSymbol || defaultTokenFrom.tokenSymbol}
                                )
                            </span>
                        )}
                    </span>
                )}
            </div>
           {/*  <div className="flex items-center gap-1"> */}
                {!showTimeWithChildren && (
                    <span className="text-xs text-font-primary/50 flex-shrink-0 whitespace-nowrap">
                        {formatCoMessageTime(notification.createTime)}
                    </span>
                )}
                {/* {
                notification.messageType === 5 && (
                    <div className='w-8 h-8 rounded-full flex items-center justify-center bg-background-3/10 border border-button-border/50'>
                        <Gift className="w-4 h-4 text-orange-500" />
                    </div>
                    )
                }
                {
                    [6, 7].includes(notification.messageType) && (
                        <div className='w-8 h-8 rounded-full flex items-center justify-center bg-background-3/10 border border-button-border/50 text-yellow-400'>
                            <Coins className="w-4 h-4 text-yellow-400" />
                        </div>
                    )
                } */}
            {/* </div>    */}    
        </div>
    )
}